import omit from "lodash/omit"
import uniqueId from "lodash/uniqueId"

import { debounceBy } from "~/lib/utils"
import { parseListKey } from "~/modules/list/utils"
import ListService from "~/modules/list/ListService"

export const DEFAULT_INSTANCE = {
    items: [],
    parentItem: undefined,
    status: "waiting", // don't set ready until it has items
    nextCursor: undefined,
    end: false, // have we reached the end?
    filters: {},
    operators: {},
    orderBy: undefined,
    direction: undefined,
    includeTotalCount: false,
}

export class ListNotFoundError extends Error {
    constructor(listId) {
        super(`The list ${listId} was not found`)
    }
}

/**
 * Check docs related to this module at guides/blocks.md
 */

export function makeState() {
    return {
        descriptions: {}, // listId  => ListDescription
        instances: {}, // listKey => ListInstance
        lastRequest: {}, // listKey => string
    }
}

export const mutations = {
    setLastRequest(state, { key, requestId }) {
        state.lastRequest[key] = requestId
    },

    setDescription(state, description) {
        const listId = description.listId
        if (listId in state.descriptions) {
            for (const field in description) {
                state.descriptions[listId][field] = description[field]
            }
        } else {
            state.descriptions[listId] = description
        }
    },

    setFilterDefinition(state, { key, filterDefinition }) {
        if (!(key in state.descriptions)) {
            throw new Error(
                `Failed to set filter definition for unexistent list ${key}`
            )
        }

        const description = state.descriptions[key]
        const { prop } = filterDefinition
        const idx = description.filters.findIndex(f => f.prop === prop)
        if (idx === -1) {
            throw new Error(`Failed to update unexistent filter ${prop}`)
        }
        description.filters[idx] = filterDefinition
    },

    setInstance(state, { key, instance }) {
        if (!(key in state.instances)) {
            state.instances[key] = instance
        } else {
            state.instances[key] = Object.assign(
                {},
                state.instances[key],
                instance
            )
        }
    },

    clearModule(state) {
        state.descriptions = {}
        state.instances = {}
        state.lastRequest = {}
    },

    clearAllFilterAndOrderDescriptions(state) {
        Object.values(state.descriptions).forEach(description => {
            delete description.filters
            delete description.orders
        })
    },

    setStatus(state, { key, value }) {
        const instance = state.instances[key]
        instance.status = value
    },

    updateItem(state, { item, listKey }) {
        const { key } = item._meta
        const list = state.instances[listKey]
        const itemIndex = list.items.findIndex(i => i[key] === item[key])
        if (itemIndex !== -1) {
            const newItem = Object.assign({}, list.items[itemIndex], item)
            list.items[itemIndex] = newItem
        } else {
            list.items.push(item)
        }
    },
}

// Inject debounceBy because if a debounced function fails while testing, the mocha process
// crashes!!
export function makeActions(listService, debounceByFn = debounceBy) {
    return {
        async init(
            { state, commit, dispatch },
            { key, includeTotalCount = false }
        ) {
            const { listId, parents } = parseListKey(key)

            let parentItem = undefined
            if (parents.length > 0)
                parentItem = await listService.fetchParent(parents)

            // Ensure instance exists
            if (!(key in state.instances)) {
                const instance = {
                    ...DEFAULT_INSTANCE,
                    items: [],
                    parentItem,
                    includeTotalCount,
                }
                commit("setInstance", { key, instance })
            }

            // Ensure description exists
            if (!(listId in state.descriptions)) {
                await dispatch("structure/refreshStructure", null, {
                    root: true,
                })
                if (!(listId in state.descriptions))
                    throw new ListNotFoundError(listId)
            }
        },

        /*
         * This action is debounced by key: dispatching executeRefresh with different keys will
         * not result in the last call canceling the previous one.
         */
        _executeRefresh: debounceByFn(
            ({ state, commit, dispatch }, { key, requestId, more = false }) => {
                const { listId, parents = [] } = parseListKey(key)
                const instance = state.instances[key]
                const description = state.descriptions[listId]

                commit("setStatus", { key, value: "refreshing" })
                return listService
                    .fetchItems({ description, instance, parents, more })
                    .then(
                        ({
                            items,
                            filters,
                            orders,
                            totalCount,
                            nextCursor,
                            rest,
                        }) => {
                            if (state.lastRequest[key] !== requestId) return

                            // dont't overwrite filters and orders if they exist
                            // because they could have been modified by frontend
                            commit("setDescription", {
                                filters,
                                orders,
                                ...description,
                                ...rest,
                            })
                            commit("setInstance", {
                                key,
                                instance: {
                                    items: more
                                        ? instance.items.concat(items)
                                        : items,
                                    nextCursor,
                                    end: !nextCursor,
                                    status: "ready",
                                    totalCount,
                                },
                            })
                        }
                    )
                    .catch(error => {
                        if (
                            error instanceof DOMException &&
                            error.name === "AbortError"
                        ) {
                            commit("setStatus", { key, value: "ready" })
                            return
                        }

                        dispatch(
                            "pushNotification",
                            {
                                title: error.message,
                                type: "error",
                            },
                            { root: true }
                        )

                        commit("setInstance", {
                            key,
                            instance: { status: "error" },
                        })
                    })
            },
            500,
            {},
            (_, { key }) => key
        ),

        /*
         * When `justRefresh` executes it stores, for that key, a unique identifier `requestId`.
         *
         * Next, it dispatches `_executeRefresh`, which receives `requestId` as a parameter.
         *
         * `_executeRefresh` makes a request and, when done, it checks that the received `requestId`
         * matches the one stored for the last call to `refresh`, for that key.
         *
         * If it doesn't match, the response is dropped.
         *
         * This is to avoid that two requests close in time, but not close enough for the
         * debouncing to cancel the first one, result in a brief period of time in which we are
         * showing data that doesn't come for the last request.
         */
        justRefresh({ commit, dispatch }, { key, more }) {
            const requestId = uniqueId("request_")
            commit("setLastRequest", { key, requestId })
            dispatch("_executeRefresh", { key, requestId, more })
        },

        loadMore({ dispatch }, { key }) {
            dispatch("justRefresh", { key, more: true })
        },

        setFilters(
            { commit, dispatch, state },
            { key, filters, operators = {} }
        ) {
            commit("setInstance", {
                key,
                instance: {
                    filters: {
                        ...state.instances[key]?.filters,
                        ...filters,
                    },
                    operators,
                },
            })
            dispatch("justRefresh", { key })
        },

        clearFilters({ commit, dispatch }, { key }) {
            commit("setInstance", {
                key,
                instance: {
                    filters: {},
                    operators: {},
                    orderBy: undefined,
                    direction: undefined,
                },
            })
            dispatch("justRefresh", { key })
        },

        setOrderBy({ state, commit, dispatch }, { key, orderBy }) {
            const instance = state.instances[key] || {}
            const { direction = "ASC" } = instance
            commit("setInstance", {
                key,
                instance: {
                    orderBy,
                    direction,
                },
            })
            dispatch("justRefresh", { key })
        },

        toggleDirection({ state, commit, dispatch }, { key }) {
            const instance = state.instances[key]
            let { direction } = instance
            direction = direction === "ASC" ? "DESC" : "ASC"
            commit("setInstance", { key, instance: { direction } })
            dispatch("justRefresh", { key })
        },

        removeFilter({ state, commit, dispatch }, { key, prop }) {
            const instance = state.instances[key]
            const filters = omit(instance.filters ?? {}, prop)
            commit("setInstance", {
                key,
                instance: {
                    filters,
                },
            })
            dispatch("justRefresh", { key })
        },

        updateOptions({ state, commit }, { key, filterProp, options }) {
            const descriptionKey = key.split("?")[0]
            const filters = state.descriptions[descriptionKey].filters
            const filterDefinition = filters.find(def => def.prop == filterProp)
            commit("setFilterDefinition", {
                key: descriptionKey,
                filterDefinition: {
                    ...filterDefinition,
                    options,
                },
            })
        },
    }
}

export default {
    namespaced: true,
    state: makeState(),
    mutations,
    actions: makeActions(new ListService()),
}
